/**
 * API Key 管理（FR-09 详案 §5）：列表 + 创建弹窗（模板/逐域权限网格/过期时间）
 * + 一次性明文 key 展示 + 启停/删除。
 * 完整 key 仅创建响应出现一次——关闭弹窗后无法再查看（页面警示）。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiKeyListResponse, ApiKeyScopes, ApiKeySummary } from '@chronospeak/contracts';
import { apiKeyPresets, apiKeyScopeDomains } from '@chronospeak/contracts';
import { adminHttp, ApiError } from '../lib/api';
import { Button } from '@chronospeak/ui';
import { ErrorNote, Loading, PageHeader, TableWrap } from '../components/ui-bits';

/** 只读域（与后端硬规则一致；UI 上 write 开关禁用） */
const READ_ONLY_DOMAINS = new Set(['submissions', 'settings', 'analytics']);

const DOMAIN_LABELS: Record<string, string> = {
  builders: '创作人',
  notices: '公告',
  submissions: '问卷',
  pages: '页面',
  entrances: '建筑入口',
  settings: '站点设置',
  town: '小镇场景',
  assets: '资源上传',
  analytics: '数据概览',
};

function ScopeSummary({ scopes }: { scopes: ApiKeyScopes }) {
  const parts = Object.entries(scopes).filter(([, v]) => v);
  if (parts.length === 0) return <span style={{ color: 'var(--muted)' }}>无权限</span>;
  return (
    <span style={{ fontSize: 12 }}>
      {parts.map(([d, v]) => `${DOMAIN_LABELS[d] ?? d}:${v === 'write' ? '写' : '读'}`).join(' · ')}
    </span>
  );
}

export function ApiKeysPage() {
  const [data, setData] = useState<ApiKeyListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // 创建弹窗状态
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScopes>({});
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // 一次性明文 key 展示
  const [createdKey, setCreatedKey] = useState<{ name: string; key: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await adminHttp.get<ApiKeyListResponse>('/admin/api-keys'));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载失败');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const applyPreset = (preset: 'readonly' | 'content') => {
    setScopes({ ...apiKeyPresets[preset].scopes });
  };

  const toggleScope = (domain: string, level: 'read' | 'write') => {
    setScopes((prev) => {
      const next = { ...prev };
      const current = next[domain as keyof ApiKeyScopes];
      if (level === 'read') {
        next[domain as keyof ApiKeyScopes] = current === 'read' ? null : 'read';
      } else if (!READ_ONLY_DOMAINS.has(domain)) {
        next[domain as keyof ApiKeyScopes] = current === 'write' ? null : 'write';
      }
      return next;
    });
  };

  const create = async () => {
    setFormError(null);
    if (!name.trim()) {
      setFormError('请填写 Key 名称。');
      return;
    }
    if (!Object.values(scopes).some((v) => v)) {
      setFormError('请至少授权一个功能域。');
      return;
    }
    setBusy(true);
    try {
      const res = await adminHttp.post<ApiKeySummary & { key: string }>('/admin/api-keys', {
        name: name.trim(),
        scopes,
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      });
      setCreateOpen(false);
      setCreatedKey({ name: res.name, key: res.key });
      setCopied(false);
      setName('');
      setScopes({});
      setExpiresAt('');
      await load();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : '创建失败');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id: string, action: 'disable' | 'enable') => {
    setError(null);
    setNotice(null);
    try {
      await adminHttp.post(`/admin/api-keys/${id}/${action}`);
      setNotice(action === 'disable' ? 'Key 已禁用（即时生效）。' : 'Key 已启用。');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '操作失败');
    }
  };

  const remove = async (id: string, keyName: string) => {
    if (!window.confirm(`确认删除 API Key「${keyName}」？使用此 Key 的 Agent 将立即失去访问能力。`)) return;
    setError(null);
    setNotice(null);
    try {
      await adminHttp.delete(`/admin/api-keys/${id}`);
      setNotice('Key 已删除（即时生效）。');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '删除失败');
    }
  };

  const copyKey = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
    } catch {
      // 剪贴板不可用时降级：选中文本便于手动复制
      setCopied(false);
    }
  };

  const gridDomains = useMemo(() => [...apiKeyScopeDomains], []);

  return (
    <section>
      <PageHeader
        title='API Keys'
        sub='供 AI Agent 通过 chronospeak-admin skill 调用管理 API。Key 以 cs_live_ 开头，仅创建时完整显示一次。'
        actions={
          <Button variant='primary' size='md' onClick={() => setCreateOpen(true)}>
            新建 API Key
          </Button>
        }
      />
      {error && <ErrorNote message={error} />}
      {notice && (
        <p role='status' className='mgr-notice'>
          {notice}
        </p>
      )}
      {!data && !error && <Loading />}
      {data && (
        <TableWrap>
          <thead>
            <tr>
              <th scope='col'>名称</th>
              <th scope='col'>Key 前缀</th>
              <th scope='col'>权限</th>
              <th scope='col'>状态</th>
              <th scope='col'>最近使用</th>
              <th scope='col'>过期时间</th>
              <th scope='col' />
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: 'var(--muted)' }}>
                  还没有 API Key。点击右上角「新建 API Key」创建。
                </td>
              </tr>
            )}
            {data.items.map((k) => (
              <tr key={k.id}>
                <td>{k.name}</td>
                <td>
                  <code>{k.prefix}…</code>
                </td>
                <td>
                  <ScopeSummary scopes={k.scopes} />
                </td>
                <td>
                  <span className={`mgr-badge mgr-badge--${k.status === 'ACTIVE' ? 'published' : 'offline'}`}>
                    {k.status === 'ACTIVE' ? '启用' : '禁用'}
                  </span>
                </td>
                <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString('zh-CN') : '从未使用'}</td>
                <td>{k.expiresAt ? new Date(k.expiresAt).toLocaleDateString('zh-CN') : '永不过期'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {k.status === 'ACTIVE' ? (
                    <Button variant='ghost' size='sm' onClick={() => void setStatus(k.id, 'disable')}>
                      禁用
                    </Button>
                  ) : (
                    <Button variant='ghost' size='sm' onClick={() => void setStatus(k.id, 'enable')}>
                      启用
                    </Button>
                  )}{' '}
                  <Button variant='ghost' size='sm' onClick={() => void remove(k.id, k.name)}>
                    删除
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      {/* 创建弹窗 */}
      {createOpen && (
        <div className='apikeys-modal-backdrop' role='presentation'>
          <div className='apikeys-modal' role='dialog' aria-modal='true' aria-label='新建 API Key'>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: 18, margin: '0 0 12px' }}>新建 API Key</h2>
            {formError && <ErrorNote message={formError} />}
            <label className='mgr-field'>
              名称（备注用途，如「巡检机器人」）
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
            </label>
            <fieldset className='mgr-fieldset' style={{ margin: '12px 0' }}>
              <legend>预设模板</legend>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant='ghost' size='sm' onClick={() => applyPreset('readonly')}>
                  只读巡检
                </Button>
                <Button variant='ghost' size='sm' onClick={() => applyPreset('content')}>
                  内容管理
                </Button>
              </div>
            </fieldset>
            <fieldset className='mgr-fieldset' style={{ margin: '12px 0' }}>
              <legend>权限（功能域 × 读/写）</legend>
              <div className='apikeys-scope-grid'>
                <span />
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>读</span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>写</span>
                {gridDomains.map((d) => (
                  <span key={d} className='apikeys-scope-row' style={{ display: 'contents' }}>
                    <span style={{ fontSize: 13 }}>{DOMAIN_LABELS[d] ?? d}</span>
                    <input
                      type='checkbox'
                      aria-label={`${DOMAIN_LABELS[d] ?? d} 读权限`}
                      checked={scopes[d as keyof ApiKeyScopes] !== null && scopes[d as keyof ApiKeyScopes] !== undefined}
                      onChange={() => toggleScope(d, 'read')}
                    />
                    <input
                      type='checkbox'
                      aria-label={`${DOMAIN_LABELS[d] ?? d} 写权限${READ_ONLY_DOMAINS.has(d) ? '（此域只读）' : ''}`}
                      disabled={READ_ONLY_DOMAINS.has(d)}
                      title={READ_ONLY_DOMAINS.has(d) ? '该域仅支持只读' : undefined}
                      checked={scopes[d as keyof ApiKeyScopes] === 'write'}
                      onChange={() => toggleScope(d, 'write')}
                    />
                  </span>
                ))}
              </div>
            </fieldset>
            <label className='mgr-field'>
              过期时间（可选，留空 = 永不过期）
              <input type='datetime-local' value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant='ghost' size='md' onClick={() => setCreateOpen(false)}>
                取消
              </Button>
              <Button variant='primary' size='md' onClick={() => void create()} disabled={busy}>
                创建
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 一次性明文 key 展示弹窗 */}
      {createdKey && (
        <div className='apikeys-modal-backdrop' role='presentation'>
          <div className='apikeys-modal' role='dialog' aria-modal='true' aria-label='API Key 已创建'>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: 18, margin: '0 0 8px' }}>API Key 已创建</h2>
            <p className='mgr-error' role='alert'>
              <span aria-hidden='true'>✎ </span>
              请立即复制保存——关闭本弹窗后完整 Key 将无法再次查看。
            </p>
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>Key 名称：{createdKey.name}</p>
            <div className='apikeys-key-reveal'>
              <code style={{ wordBreak: 'break-all', fontSize: 13 }}>{createdKey.key}</code>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant='primary' size='md' onClick={() => void copyKey()} disabled={copied}>
                {copied ? '已复制 ✓' : '复制 Key'}
              </Button>
              <Button variant='ghost' size='md' onClick={() => setCreatedKey(null)}>
                我已保存，关闭
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
